import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  setAllSelectedElementKeys,
  setSelectedElementKey,
} from "@/store/iframeSlice";
import {
  useCanvasStore,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useDetachInstance } from "@/hooks/useDetachInstance";
import { useCreateComponent } from "@/hooks/canvas/useCreateComponent";
import { useGroupElements } from "@/hooks/canvas/useGroupElements";
import { useWrapStoryboards } from "@/hooks/storyboards/useWrapStoryboards";
import { usePasteToReplace } from "@/components/Canvas/hooks/useCopyPaste";
import { useSelectedElements } from "@/hooks/useSelectedElements";
import { TempoElement } from "@/utils/tempoElement";
import { SKIP_ROOT_CODEBASE_ID } from "@/utils/constants";
import { useKeyboardShortcuts } from "../core/useKeyboardShortcuts";
import { ShortcutMap } from "../core/types";
import { useActiveCanvas } from "@/utils/utils";

export const useCanvasSelectionShortcuts = () => {
  const dispatch = useDispatch();
  const { selectedElement, selectedNavTreeNode } = useSelectedNavTreeNode();
  const { selectedStoryboards } = useSelectedElements();

  const createComponent = useCreateComponent();
  const groupElements = useGroupElements();
  const wrapStoryboards = useWrapStoryboards();
  const detachInstance = useDetachInstance();
  const pasteToReplace = usePasteToReplace();

  const activeCanvas = useActiveCanvas();
  const temporaryStoryboard = useCanvasStore(
    (state) => state.temporaryStoryboard,
  );

  // Select all elements
  const handleSelectAll = useCallback(() => {
    const currentStoryboards = (activeCanvas?.storyboards || []).filter(
      (sb) => !sb.deleted,
    );
    const storyboardsToRender = temporaryStoryboard
      ? [...currentStoryboards, temporaryStoryboard]
      : currentStoryboards;

    if (!storyboardsToRender?.length) return;

    const visibleStoryboardIds = storyboardsToRender
      .map((storyboard) => storyboard.id)
      .filter(Boolean);

    if (visibleStoryboardIds.length === 0) return;

    if (visibleStoryboardIds.length === 1) {
      dispatch(
        setSelectedElementKey(
          TempoElement.forStoryboard(visibleStoryboardIds[0]).getKey(),
        ),
      );
    } else {
      dispatch(
        setAllSelectedElementKeys({
          multiSelectedElementKeys: visibleStoryboardIds.map((id) =>
            TempoElement.forStoryboard(id).getKey(),
          ),
          selectedElementKey: TempoElement.forStoryboard(
            visibleStoryboardIds[0],
          ).getKey(),
        }),
      );
    }
  }, [dispatch, activeCanvas?.storyboards, temporaryStoryboard]);

  // Navigate to sibling
  const handleNavigateToSibling = useCallback(
    (direction: "next" | "previous") => {
      if (!selectedNavTreeNode?.parent?.children?.length) return;

      const siblings = selectedNavTreeNode.parent.children;
      const currentIndex = siblings.findIndex((child) =>
        child.tempoElement.isEqual(selectedElement),
      );
      if (currentIndex === -1) return;

      let targetIndex =
        direction === "next" ? currentIndex + 1 : currentIndex - 1;

      // Circular navigation
      if (targetIndex >= siblings.length) {
        targetIndex = 0;
      } else if (targetIndex < 0) {
        targetIndex = siblings.length - 1;
      }

      if (siblings[targetIndex]) {
        dispatch(
          setSelectedElementKey(siblings[targetIndex].tempoElement.getKey()),
        );
      }
    },
    [selectedNavTreeNode, selectedElement, dispatch],
  );

  // Navigate to parent
  const handleNavigateToParent = useCallback(() => {
    if (!selectedNavTreeNode?.parent) return;

    // Skip root nodes
    if (
      selectedNavTreeNode.parent.tempoElement.codebaseId ===
      SKIP_ROOT_CODEBASE_ID
    ) {
      return;
    }

    const parentElement = selectedNavTreeNode.parent.tempoElement;
    if (parentElement && !parentElement.isEmpty()) {
      dispatch(setSelectedElementKey(parentElement.getKey()));
    }
  }, [selectedNavTreeNode, dispatch]);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    dispatch(setSelectedElementKey(null));
  }, [dispatch]);

  // Handles both storyboards and elements
  const handleWrapInDiv = useCallback(() => {
    if (selectedStoryboards.length > 0) {
      wrapStoryboards({ sourceStoryboards: selectedStoryboards });
    } else {
      groupElements();
    }
  }, [selectedStoryboards, wrapStoryboards, groupElements]);

  // Shortcut map with explicit key-handler mapping
  const shortcuts: ShortcutMap = useMemo(
    () => ({
      "mod+a": {
        handler: handleSelectAll,
        description: "Select all elements",
      },
      tab: {
        handler: () => handleNavigateToSibling("next"),
        description: "Select next sibling",
      },
      "shift+tab": {
        handler: () => handleNavigateToSibling("previous"),
        description: "Select previous sibling",
      },
      "\\": {
        handler: handleNavigateToParent,
        description: "Select parent",
      },
      escape: {
        handler: handleClearSelection,
        description: "Clear selection",
      },
      "mod+alt+k": {
        handler: createComponent,
        description: "Create component from selected element",
      },
      "mod+alt+b": {
        handler: detachInstance,
        description: "Detach component instance",
      },
      "mod+alt+g": {
        handler: handleWrapInDiv,
        description: "Group (wrap) selected elements",
      },
      "mod+shift+r": {
        handler: pasteToReplace,
        description: "Paste to replace selected elements",
      },
    }),
    [
      handleSelectAll,
      handleNavigateToSibling,
      handleNavigateToParent,
      handleClearSelection,
      createComponent,
      detachInstance,
      handleWrapInDiv,
      pasteToReplace,
    ],
  );

  useKeyboardShortcuts({ shortcuts });
};
